import React, { Suspense } from "react";
import { Box, Typography, useTheme, alpha, Skeleton } from "@mui/material";
import { ChartContainer } from "./ChartContainer";
import { EmptyState } from "../EmptyState/EmptyState";
import { theme } from "../../../../theme";
import moment from "moment";

// Lazy load the chart library to improve initial load time
const ReactApexChart = React.lazy(() => import("react-apexcharts"));

export interface AreaChartProps {
  title: string;
  icon?: string;
  data: any[];
  xAxisKey: string;
  yAxisKey: string;
  height?: number;
  subtitle?: string;
  action?: React.ReactNode;
  isLoading?: boolean;
  emptyStateMessage?: string;
  formatYAxis?: (value: number) => string;
  formatTooltip?: (value: number) => string;
  delay?: number;
}

export const AreaChart: React.FC<AreaChartProps> = ({
  title,
  icon = "show_chart",
  data,
  xAxisKey,
  yAxisKey,
  height = 350,
  subtitle,
  action,
  isLoading = false,
  emptyStateMessage = "No data available",
  formatYAxis,
  formatTooltip,
  delay = 0,
}) => {
  const theme = useTheme();

  // Chart options
  const chartOptions = {
    chart: {
      type: "area",
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
    },
    colors: ["#0891B2"],
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: "smooth",
      width: 2,
    },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.3,
        opacityTo: 0.1,
        stops: [0, 90, 100],
      },
    },
    xaxis: {
      categories: data.map((item) => item[xAxisKey]),
      labels: {
        style: {
          colors: theme.palette.text.secondary,
          fontSize: "12px",
        },
        rotate: -45,
        rotateAlways: true,
        hideOverlappingLabels: true,
        trim: true,
        maxHeight: 120,
        offsetY: 5,
        formatter: (value: string) => {
          // Format date to be more compact
          return moment(value).format("MMM D");
        },
      },
      tickPlacement: "on",
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      tooltip: {
        enabled: true,
      },
    },
    yaxis: {
      labels: {
        formatter: formatYAxis || ((value) => Math.round(value).toString()),
        style: {
          colors: theme.palette.text.secondary,
        },
      },
    },
    tooltip: {
      y: {
        formatter: formatTooltip || ((value) => Math.round(value).toString()),
      },
    },
    grid: {
      borderColor: alpha(theme.palette.divider, 0.1),
      strokeDashArray: 4,
      padding: {
        bottom: 15,
      },
    },
    responsive: [
      {
        breakpoint: 600,
        options: {
          xaxis: {
            labels: {
              rotate: -90,
              offsetY: 0,
              style: {
                fontSize: "10px",
              },
            },
          },
          chart: {
            height: 350,
          },
        },
      },
    ],
  };

  // Prepare series data
  const series = [
    {
      name: title,
      data: data.map((item) => item[yAxisKey]),
    },
  ];

  // Chart with suspense for loading state
  const ChartWithSuspense = () => (
    <Suspense
      fallback={
        <Box sx={{ height, width: "100%" }}>
          <Skeleton
            variant="rectangular"
            height={height}
            sx={{
              borderRadius: 2,
              transform: "none",
            }}
          />
        </Box>
      }
    >
      <ReactApexChart
        options={chartOptions}
        series={series}
        type="area"
        height={height}
      />
    </Suspense>
  );

  // Empty state component
  const EmptyStateComponent = (
    <EmptyState
      icon="show_chart"
      title="No Data Available"
      message={emptyStateMessage}
      height={height}
    />
  );

  return (
    <ChartContainer
      title={title}
      icon={icon}
      subtitle={subtitle}
      action={action}
      isLoading={false}
      emptyState={data.length === 0 ? EmptyStateComponent : undefined}
      delay={delay}
    >
      {isLoading ? (
        <Box sx={{ height, width: "100%" }}>
          <Skeleton
            variant="rectangular"
            height={height}
            sx={{
              borderRadius: 2,
              transform: "none",
            }}
          />
        </Box>
      ) : (
        <ChartWithSuspense />
      )}
    </ChartContainer>
  );
};
